import { useRef, useState, useEffect } from 'react';
import { motion, useMotionValue, useAnimationFrame, useReducedMotion } from 'framer-motion';

interface TrackItem {
  id: string;
  name: string;
  artist: string;
  image: string;
  spotifyUrl: string;
  duration: string;
}

interface InfiniteTrackScrollProps {
  tracks: TrackItem[];
  accentColor: string;
  spotifyArtistUrl?: string;
}

const CARD_SIZE_DESKTOP = 220;
const CARD_SIZE_MOBILE = 160;
const GAP = 16;
// px per second
const SCROLL_SPEED = 80;

const TrackCard = ({
  track,
  accentColor,
  isMobile,
}: {
  track: TrackItem;
  accentColor: string;
  isMobile: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const size = isMobile ? CARD_SIZE_MOBILE : CARD_SIZE_DESKTOP;

  return (
    <a
      href={track.spotifyUrl}
      target="_blank"
      rel="noopener noreferrer"
      draggable={false}
      style={{
        display: 'block',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: 16,
        overflow: 'hidden',
        position: 'relative',
        textDecoration: 'none',
        boxShadow: hovered
          ? `0 0 0 2px ${accentColor}, 0 8px 32px rgba(0,0,0,0.22)`
          : '0 2px 12px rgba(0,0,0,0.10)',
        transform: !isMobile && hovered ? 'scale(1.12) translateY(-10px)' : 'scale(1) translateY(0)',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        willChange: 'transform',
        zIndex: hovered ? 10 : 1,
      }}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
    >
      {track.image ? (
        <img
          src={track.image}
          alt={track.name}
          loading="lazy"
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', background: '#1e293b' }} />
      )}

      {/* Hover info overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 12,
        opacity: hovered ? 1 : 0,
        transform: hovered ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.22s ease, transform 0.22s ease',
      }}>
        <p style={{
          color: 'white', fontWeight: 700, fontSize: 13, lineHeight: 1.3, margin: 0,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
        }}>{track.name}</p>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, margin: '3px 0 0' }}>{track.duration}</p>
        <p style={{ color: accentColor, fontSize: 11, fontWeight: 600, margin: '4px 0 0' }}>Öppna Spotify →</p>
      </div>

      {/* Default (non-hover) name label */}
      {!hovered && (
        <>
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
          }} />
          <p style={{
            position: 'absolute', bottom: 10, left: 10, right: 10,
            color: 'white', fontWeight: 600, fontSize: 12, margin: 0,
            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          }}>{track.name}</p>
        </>
      )}
    </a>
  );
};

const InfiniteTrackScroll = ({ tracks, accentColor, spotifyArtistUrl }: InfiniteTrackScrollProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const isPaused = useRef(false);   // hover pause
  const isDragging = useRef(false); // drag pause

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const cardSize = isMobile ? CARD_SIZE_MOBILE : CARD_SIZE_DESKTOP;
  const totalWidth = tracks.length * (cardSize + GAP);
  const tripled = [...tracks, ...tracks, ...tracks];

  const x = useMotionValue(0);

  // Auto-scroll via animation frame — shares `x` with drag, no conflicts
  useAnimationFrame((_, delta) => {
    if (prefersReducedMotion || isPaused.current || isDragging.current) return;
    const next = x.get() - (SCROLL_SPEED * delta) / 1000;
    // Seamlessly reset after one full set width
    x.set(next <= -totalWidth ? next + totalWidth : next);
  });

  if (tracks.length === 0) return null;

  return (
    <section style={{ marginBottom: 48 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Topplåtar</h2>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            Hovra för info · Dra för att scrolla · Klicka för att lyssna på Spotify
          </p>
        </div>
        {spotifyArtistUrl && (
          <a
            href={spotifyArtistUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, fontWeight: 600, color: accentColor, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Visa alla →
          </a>
        )}
      </div>

      <div
        style={{ position: 'relative', overflow: 'hidden' }}
        onMouseEnter={() => { isPaused.current = true; }}
        onMouseLeave={() => { isPaused.current = false; }}
      >
        <motion.div
          style={{ display: 'flex', gap: GAP, x, paddingBottom: 16, userSelect: 'none' }}
          drag="x"
          dragConstraints={{ left: -totalWidth * 2, right: 0 }}
          dragElastic={0.05}
          dragMomentum={false}
          onDragStart={() => { isDragging.current = true; }}
          onDragEnd={() => {
            isDragging.current = false;
            isPaused.current = false;
          }}
          whileDrag={{ cursor: 'grabbing' }}
        >
          {tripled.map((track, i) => (
            <TrackCard
              key={`${track.id}-${i}`}
              track={track}
              accentColor={accentColor}
              isMobile={isMobile}
            />
          ))}
        </motion.div>

        <div style={{
          position: 'absolute', top: 0, left: 0, bottom: 16, width: 100,
          pointerEvents: 'none', zIndex: 2,
          background: 'linear-gradient(to right, white 0%, transparent 100%)',
        }} />
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 16, width: 100,
          pointerEvents: 'none', zIndex: 2,
          background: 'linear-gradient(to left, white 0%, transparent 100%)',
        }} />
      </div>
    </section>
  );
};

export default InfiniteTrackScroll;
export type { TrackItem };
